import React from 'react'
import {VStack, Text, TextProps, useMultiStyleConfig, Skeleton, SkeletonText} from '@chakra-ui/react'

interface HeadlineProps {
    title?: string | React.ReactNode
    withBanner?: boolean
    variant?: 'wishlist'
    children?: string | React.ReactNode
}

/**
 * @name Headline
 *
 * @component
 *
 * @description
 * Renders title and description of the page.
 *
 * @param {HeadlineProps} props - Properties object.
 * @param {string | React.ReactNode} props.title Title to be rendered in the headline.
 * @param {boolean} [props.withBanner] - Indicates if there's a banner or not.
 * @param {string} [props.variant] - Variant of the headline.
 * @param {string | React.ReactNode} props.children - Description to insert in the headline.
 *
 * @returns {JSX.Element} - Rendered Headline component.
 */
const Headline = ({title, withBanner = false, variant, children}: HeadlineProps): JSX.Element => {
    const styles = useMultiStyleConfig('Headline', {variant}) as Record<string, TextProps>

    return (
        <VStack {...styles.wrapper} {...(withBanner ? styles.withBanner : {})}>
            {title && (
                <Text as="h1" {...styles.title}>
                    {title}
                </Text>
            )}
            {children && <Text {...styles.description}>{children}</Text>}
        </VStack>
    )
}

/**
 * @name HeadlineSkeleton
 *
 * @component
 *
 * @description
 * Skeleton variant for Headline component to be used for lazy loading.
 *
 * @param {string} [variant]
 *
 * @returns {JSX.Element} - Rendered HeadlineSkeleton component.
 */
const HeadlineSkeleton = ({variant, hasTitle, hasDescription}: {variant?: string; hasTitle?: boolean; hasDescription?: boolean}): JSX.Element => {
    const styles = useMultiStyleConfig('Headline', {variant}) as Record<string, TextProps>

    return (
        <VStack {...styles.wrapper} data-testid="headline-skeleton">
            {hasTitle && <Skeleton {...styles.skeletonTitle} />}
            {hasDescription && <SkeletonText noOfLines={variant === 'wishlist' ? 1 : 3} {...styles.skeletonDescription} />}
        </VStack>
    )
}

export {Headline, HeadlineSkeleton}
